// Per-mission tickets for the RICH project-view board (list / table / board /
// roadmap). The board expects the full sampleData story shape ; here we build
// that shape deterministically from compact, generated ticket content
// (board-data.js) so each mission shows coherent tickets instead of the shared
// generic sample. Assignees are resolved against the mission's own squad.

import { squadFor } from './mission-context.js';
import { boardByDoor, boardByMission } from './board-data.js';
// Life Connect (adb) demo missions carry their OWN board tickets (generated by
// .claude/seed-life-connect.mjs) so each mission's Stories tab is coherent with
// its name/type instead of falling back to a generic door spec.
import { BOARD_BY_MISSION_LC } from './lc-overview.generated.js';
import { isDemoMission, isDemoMissionId } from './mock-scope.js';
const boardByMissionAll = { ...boardByMission, ...BOARD_BY_MISSION_LC };

const PROGRESS = { backlog: 0, planned: 12, in_progress: 55, blocked: 30, in_review: 85, done: 100, cancelled: 0 };
const PHASES = ['Discovery', 'Planning', 'Implementation', 'Review'];
const LABEL_PALETTE = ['#6366f1', '#0ea5e9', '#ec4899', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#14b8a6'];

const slug = (name) => 'mem-' + String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
const hash = (s) => { let h = 0; for (const c of String(s)) h = (h * 31 + c.charCodeAt(0)) >>> 0; return h; };
const hueOf = (s) => hash(s) % 360;
const colorFor = (l) => LABEL_PALETTE[hash(l) % LABEL_PALETTE.length];

function membersFor(mission) {
  const sq = squadFor(mission) ?? {};
  const agents = [sq.lead, ...(sq.permanent || []), ...(sq.temporary || [])].filter(Boolean).map((m) => ({
    id: slug(m.name), kind: 'agent', display_name: m.name, role: m.role, hourly_rate: 12, avatar_hue: hueOf(m.name),
  }));
  agents.push({ id: 'mem-greg', kind: 'human', display_name: 'Greg', role: 'PM', hourly_rate: 140, avatar_hue: 210 });
  return agents;
}

function dateAt(dayOffset) {
  const d = new Date(Date.UTC(2026, 4, 4));
  d.setUTCDate(d.getUTCDate() + dayOffset);
  return d.toISOString().slice(0, 10);
}

export function buildBoardStories(mission, spec) {
  if (!spec?.tickets?.length) return null;
  const members = membersFor(mission);
  const byName = (n) => members.find((m) => m.display_name === n) ?? members[0];
  const prefix = spec.prefix || 'TKT';
  const ids = spec.tickets.map((_, i) => `${mission.id}-s${i + 1}`);

  return spec.tickets.map((tk, i) => {
    const status = tk.status || 'backlog';
    const est = 8 + (i % 5) * 2;
    const start = i * 3;
    return {
      id: ids[i],
      key: `${prefix}-${i + 1}`,
      title: tk.title,
      phase: PHASES.includes(tk.phase) ? tk.phase : PHASES[i % 4],
      description_md: tk.description || '',
      status,
      priority: tk.priority || 'medium',
      progress: PROGRESS[status] ?? 0,
      assignees: tk.assignee ? [byName(tk.assignee)] : [],
      labels: (tk.labels || []).map((l) => ({ name: l, color: colorFor(l) })),
      start_date: dateAt(start),
      due_on: dateAt(start + 4),
      estimated_hours: est,
      logged_seconds: Math.round(((PROGRESS[status] ?? 0) / 100) * est * 3600),
      mission_id: mission.id,
      mission_title: mission.title,
      // Git-graph metadata (optional, per ticket) : `branch` lane + `parent`
      // (1-based ticket index) to render a ticket's tasks as a sub-branch.
      branch: tk.branch,
      parent_id: tk.parent ? (ids[tk.parent - 1] ?? null) : null,
      depends_on: (tk.dependsOn || []).map((d) => ids[d - 1]).filter(Boolean),
      fixed_cost: 0,
      reporter_id: 'mem-greg',
      created_at: '2026-05-01T09:00:00Z',
      updated_at: '2026-06-10T16:00:00Z',
    };
  });
}

/** Rich board tickets for a mission — per-mission override → door default → null.
 *  Demo-only: outside Life Connect a mission shows its REAL stories (or none). */
export function boardStoriesFor(mission) {
  if (!isDemoMission(mission)) return null;
  const spec = boardByMissionAll[mission?.id] ?? boardByDoor[mission?.door_type];
  return spec ? buildBoardStories(mission, spec) : null;
}

// Resolve board mock stories by mission id WITHOUT a rendered panel — so the
// self-fetching StoryDrawer can detect a mock story on its first render (and
// skip the 404-bound API round-trip). Memoized; built from the per-mission spec
// with a minimal mission stub (the panel later re-registers with the real
// title). Only per-mission specs have stable ids; door-fallback stories don't.
const _builtByMission = new Map();
export function boardStoriesByMissionId(missionId) {
  if (!isDemoMissionId(missionId)) return null;
  if (_builtByMission.has(missionId)) return _builtByMission.get(missionId);
  const spec = boardByMissionAll[missionId];
  const built = spec ? buildBoardStories({ id: missionId }, spec) : null;
  _builtByMission.set(missionId, built);
  return built;
}

/** A single board mock story by its id (`<missionId>-s<n>`), or null. */
export function deriveMockStory(storyId) {
  if (typeof storyId !== 'string') return null;
  const m = /^(.+)-s\d+$/.exec(storyId);
  if (!m) return null;
  return boardStoriesByMissionId(m[1])?.find((s) => s.id === storyId) ?? null;
}
